

import { Zap, Handshake, BadgeDollarSign, ExternalLink, ShieldAlert } from 'lucide-react';
import { useCallback, useMemo, memo } from 'react';
import './animations.css';
import { useRouter } from 'next/navigation';
import { cn } from '@/src/lib/shadcn/cn';

export interface AddResource {
    icon: JSX.Element;
    text: string;
    description: string;
    disabled?: boolean;
    href?: string;
    newTabHref?: string;
    badge?: string;
}

export const addResources: AddResource[] = [
    {
        icon: <Zap className="w-4 h-4" />,
        text: "Traffic Source Management",
        description: "Connect or edit traffic sources",
        href: "/dashboard/traffic-sources",
        newTabHref: "/dashboard/traffic-sources"
    },
    {
        icon: <ShieldAlert className="w-4 h-4" />,
        text: "Blocked Traffic Rules",
        description: "Set and edit traffic blocking rules",
        href: "/dashboard/blocked-traffic",
        newTabHref: "/dashboard/blocked-traffic"
    },
    {
        icon: <Handshake className="w-4 h-4" />,
        text: "Affiliate Network Management",
        description: "Connect or edit affiliate networks",
        href: "/dashboard/affiliate-networks",
        newTabHref: "/dashboard/affiliate-networks"
    },
    {
        icon: <BadgeDollarSign className="w-4 h-4" />,
        text: "Offer Management",
        description: "Connect or edit offers",
        href: "/dashboard/offers",
        newTabHref: "/dashboard/offers"
    }
];

interface AddResourcesProps {
    searchQuery: string;
    onClose: () => void;
}

const filterAddResources = (items: AddResource[], query: string) => {
    if (!query) return items;
    const lowerQuery = query.toLowerCase();
    return items.filter(item =>
        item.text?.toLowerCase().includes(lowerQuery) ||
        item.description?.toLowerCase().includes(lowerQuery)
    );
};

function ResourceButton({ action, onResourceClick }: { action: AddResource, onResourceClick: (action: AddResource) => void }) {
    const handleClick = useCallback(() => {
        if (!action.disabled) {
            onResourceClick(action);
        }
    }, [action, onResourceClick]);

    const handleNewTabClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (action.newTabHref) {
            window.open(action.newTabHref, '_blank');
        }
    }, [action]);

    return (
        <button
            className={cn(
                "flex items-start gap-3 w-full text-left px-3 py-2 rounded-lg group",
                action.disabled
                    ? 'opacity-30 cursor-not-allowed bg-gray-50 dark:bg-gray-800/30'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700/50',
                "text-gray-700 dark:text-gray-300 text-sm",
                "resource-item resource-item-visible"
            )}
            onClick={handleClick}
            disabled={action.disabled}
        >
            <span className="mt-0.5 text-gray-500 dark:text-gray-400">{action.icon}</span>
            <div className="flex-1">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {action.text}
                        {action.disabled && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-full">
                                Disabled
                            </span>
                        )}
                    </div>
                    {action.newTabHref && !action.disabled && (
                        <button
                            onClick={handleNewTabClick}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                        >
                            <ExternalLink className="w-3 h-3 text-gray-500" />
                        </button>
                    )}
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">{action.description}</span>
            </div>
        </button>
    );
}

const MemoizedResourceButton = memo(ResourceButton);

export default function AddResources({ searchQuery, onClose }: AddResourcesProps) {
    const router = useRouter();
    const filteredAddResources = useMemo(() =>
        filterAddResources(addResources, searchQuery),
        [searchQuery]
    );

    if (!filteredAddResources.length) {
        return null;
    }

    const handleResourceClick = useCallback((action: AddResource) => {
        if (action.disabled) return;

        if (action.href) {
            router.push(action.href);
            onClose();
            return;
        }
        onClose();
    }, [router, onClose]);

    return (
        <div>
            <div className="flex items-center gap-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Quick Setup
                </h3>
                <div className="h-[1px] flex-1 bg-gradient-to-r from-gray-200 to-transparent dark:from-gray-700" />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
                {filteredAddResources.map((action, index) => (
                    <MemoizedResourceButton
                        key={index}
                        action={action}
                        onResourceClick={handleResourceClick}
                    />
                ))}
            </div>
        </div>
    );
} 